// hooks/usePostSave.ts — "save this post into a folder" for the video watch page's Save
// (1.27.50). Reads the bookmark store reactively and wraps the service mutations, following
// useBookmarks.ts (components never import the store or service directly).
//
// Watch later is the Videos page's built-in folder and is created on its first use, so the
// first save into it goes through the Videos endpoint; everything else is plain bookmarks.
import { useCallback, useEffect } from 'react';
import { useBookmarkStore } from '@/core/stores/bookmarkStore';
import { bookmarksService } from '@/core/services/bookmarksService';
import { videoFeedService } from '@/core/services/videoFeedService';
import type { Bookmark, BookmarkFolder } from '@/core/models/bookmark';
import type { Post } from '@/core/models/post';

export const WATCH_LATER_FOLDER_ID = 'watch-later';
const NOT_LISTED = new Set(['unsorted', WATCH_LATER_FOLDER_ID]);

export interface PostSaveHook {
  /** This post's bookmark, if saved. */
  bookmark: Bookmark | undefined;
  /** The name of the folder it is saved in. */
  savedIn: string | null;
  /** The member's own folders (not Watch later / Unsorted). */
  folders: BookmarkFolder[];
  saveTo: (folderId: string) => Promise<void>;
  createFolderAndSave: (name: string) => Promise<BookmarkFolder>;
  remove: () => Promise<void>;
}

export function usePostSave(post: Post, enabled: boolean): PostSaveHook {
  const bookmark = useBookmarkStore((s) =>
    s.bookmarks.find((b) => b.postId === post.id && !b.commentId),
  );
  const allFolders = useBookmarkStore((s) => s.folders);

  const refresh = useCallback(async () => {
    await Promise.all([bookmarksService.list(), bookmarksService.folders()]);
  }, []);

  // Know where this post is saved before the menu is opened (the button says "Saved").
  useEffect(() => {
    if (enabled) void refresh().catch(() => {});
  }, [enabled, post.id, refresh]);

  const saveTo = useCallback(
    async (folderId: string) => {
      const current = () =>
        useBookmarkStore.getState().bookmarks.find((b) => b.postId === post.id && !b.commentId);
      const hasWatchLater = useBookmarkStore
        .getState()
        .folders.some((f) => f.id === WATCH_LATER_FOLDER_ID);
      if (folderId === WATCH_LATER_FOLDER_ID && !hasWatchLater) {
        await videoFeedService.saveForLater(post.id);
        await refresh();
        const now = current();
        if (now && now.folderId !== WATCH_LATER_FOLDER_ID) {
          await bookmarksService.moveBookmark(now.id, WATCH_LATER_FOLDER_ID);
        }
      } else {
        const existing = current();
        if (existing) await bookmarksService.moveBookmark(existing.id, folderId);
        else await bookmarksService.add(post, folderId);
      }
      await refresh();
    },
    [post, refresh],
  );

  const createFolderAndSave = useCallback(
    async (name: string) => {
      const folder = await bookmarksService.createFolder(name);
      await saveTo(folder.id);
      return folder;
    },
    [saveTo],
  );

  const remove = useCallback(async () => {
    const existing = useBookmarkStore
      .getState()
      .bookmarks.find((b) => b.postId === post.id && !b.commentId);
    if (existing) await bookmarksService.remove(existing.id);
  }, [post.id]);

  const savedIn = bookmark
    ? bookmark.folderId === WATCH_LATER_FOLDER_ID
      ? 'Watch later'
      : (allFolders.find((f) => f.id === bookmark.folderId)?.name ?? 'Bookmarks')
    : null;

  return {
    bookmark,
    savedIn,
    folders: allFolders.filter((f) => !NOT_LISTED.has(f.id)),
    saveTo,
    createFolderAndSave,
    remove,
  };
}
